import {
  addDays,
  isOnCourt,
  isUnavailable,
  playerStatus,
  sortSessions,
  startOfWeek,
} from '@topspin/shared';
import type { Session } from '@topspin/shared';
import type { GroupView } from './data.js';

/** One card on the home screen: a group, and the match it has that week. */
export interface MatchRef {
  group: GroupView;
  session: Session;
}

/** A heading and the cards under it. */
export interface HomeBlock {
  title: string;
  matches: MatchRef[];
}

/**
 * What the home screen shows, as blocks of cards, by where in the season today falls:
 *
 * - **before it opens** — one block holding week 1, headed "Next Week" in the week right
 *   before it opens and "Coming Up" any earlier than that. Further out, a relative name would
 *   be measuring from a week with no tennis in it; one week out it's the plainest way to say
 *   how close the season is.
 * - **during it** — "This Week" and "Next Week".
 * - **its last week** — "This Week" alone, because there is no next week to show.
 * - **after it** — nothing, leaving the home screen to the link to the season schedule.
 *
 * Weeks start on Sunday ({@link startOfWeek}), so the Saturday after a Tuesday match still
 * calls it "This Week"; it's Sunday before "This Week" names a match nobody has played.
 */
export function homeBlocks(groups: GroupView[], today: string): HomeBlock[] {
  const matches: MatchRef[] = groups
    .flatMap((group) => sortSessions(group.sessions).map((session) => ({ group, session })))
    .sort((a, b) => a.session.date.localeCompare(b.session.date));

  const first = matches[0];
  const last = matches[matches.length - 1];
  if (!first || !last) return [];

  const thisWeek = startOfWeek(today);
  const inWeek = (start: string) =>
    matches.filter((m) => startOfWeek(m.session.date) === start);
  const nextWeek = addDays(thisWeek, 7);
  const comingUp = (from: string): HomeBlock[] => [
    { title: from === nextWeek ? 'Next Week' : 'Coming Up', matches: inWeek(from) },
  ];

  if (thisWeek < startOfWeek(first.session.date)) {
    return comingUp(startOfWeek(first.session.date));
  }
  if (thisWeek > startOfWeek(last.session.date)) return [];

  const blocks = [
    { title: 'This Week', matches: inWeek(thisWeek) },
    { title: 'Next Week', matches: inWeek(nextWeek) },
  ].filter((block) => block.matches.length > 0);
  if (blocks.length > 0) return blocks;

  // Inside the season, but this week and next are both empty — a gap the schedule doesn't
  // spell out as no-session dates. Name the next week that has tennis in it rather than
  // rendering two empty headings. It's always two or more weeks out, since a match next week
  // would have filled a block above, so this always reads "Coming Up".
  const ahead = matches.find((m) => startOfWeek(m.session.date) > thisWeek);
  return ahead ? comingUp(startOfWeek(ahead.session.date)) : [];
}

/**
 * The next night this group is actually playing: the first scheduled date on or after today.
 * A date that has been played is behind us and has nothing left to remind anyone about, so
 * today's own match counts and yesterday's does not — the Wednesday after a Tuesday session
 * already looks a week ahead. `no-session` dates are skipped rather than reported as a gap,
 * because a holiday week is not the answer to "what is coming up".
 */
export function nextScheduledSession(group: GroupView, today: string): Session | undefined {
  return sortSessions(group.sessions).find((s) => s.date >= today && s.status === 'scheduled');
}

/**
 * Groups in the order their seasons open — Tuesday's before Thursday's, rather than in whatever
 * order the device happens to hold their group IDs in. The key is the first date on the
 * schedule, `no-session` dates included: the question is where the group's season starts, not
 * when it first plays. A group with no schedule at all sorts after every group that has one,
 * and ties fall back to the name so the order never depends on storage.
 */
export function groupsByScheduleStart(groups: GroupView[]): GroupView[] {
  const start = (group: GroupView) => sortSessions(group.sessions)[0]?.date ?? '9999-12-31';
  return [...groups].sort(
    (a, b) =>
      start(a).localeCompare(start(b)) || a.config.name.localeCompare(b.config.name),
  );
}

export function overrideFor(group: GroupView, date: string, playerId: string) {
  return group.overrides.find((o) => o.date === date && o.outPlayerId === playerId);
}

/**
 * The override this player is the sub on, if any — the commitment they'd be handing off. Only a
 * reserve can hold one, and only one per session (§6.1), so the first match is the only match.
 */
export function subCommitmentFor(group: GroupView, date: string, playerId: string) {
  return group.overrides.find(
    (o) => o.date === date && o.subKind === 'reserve' && o.subPlayerId === playerId,
  );
}

export function isInGroup(group: GroupView, playerId: string): boolean {
  return group.config.players.some((p) => p.id === playerId);
}

/**
 * Where the signed-in player stands on a match, reduced to the three answers worth colouring a
 * card over (§6.2): `in` they're on court, `sub` they're a reserve who can still be asked, `out`
 * they aren't coming. Undefined for a no-session date and for anyone not in that night's
 * session — neither has a status to report, so their card stays the neutral one.
 *
 * `out` covers both ways of not coming: an override arranged in their name, and a plain "I'm not
 * available" from a reserve. Saying "I'm available now" after a sub took the slot lands on `sub`
 * rather than back on `in`, which is the truth of it — the sub keeps the court slot.
 */
export type MatchStatus = 'in' | 'sub' | 'out';

export function matchStatus(
  group: GroupView,
  session: Session,
  playerId: string,
): MatchStatus | undefined {
  if (session.status !== 'scheduled') return undefined;
  const status = playerStatus(session, group.overrides, playerId);
  if (status === 'not-in-session') return undefined;
  if (isOnCourt(status)) return 'in';
  return isUnavailable(session, group.overrides, group.availabilities, playerId) ? 'out' : 'sub';
}
